//TermsOfServiceModal.js
import React, { useState } from 'react';
import { X, FileText, ChevronDown, ChevronUp, CreditCard, Shield, Server, Users, AlertTriangle, Phone } from 'lucide-react';
import Button from '../../Button/Button';

const TermsOfServiceModal = ({ isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      // If the section is already open, close it
      if (prev[section]) {
        return {
          ...prev,
          [section]: false
        };
      }
      
      // Otherwise, close all sections and open only the clicked one
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[section] = true;
      
      return newState;
    });
  };

  // Check if any section is expanded to determine scroll behavior
  const hasExpandedSections = Object.values(expandedSections).some(Boolean);

  const CollapsibleSection = ({ title, children, sectionKey, icon: Icon }) => (
    <div className="mb-3 group">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full text-left p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 border border-gray-200 hover:border-blue-200 shadow-sm hover:shadow-md"
      >
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-white shadow-sm group-hover:bg-blue-100 transition-colors duration-300">
            <Icon className="w-4 h-4 text-blue-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 group-hover:text-blue-700 transition-colors duration-300">{title}</h3>
        </div>
        <div className="flex items-center">
          {expandedSections[sectionKey] ? (
            <ChevronUp className="w-4 h-4 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
          )}
        </div>
      </button>
      {expandedSections[sectionKey] && (
        <div className="mt-2 mx-2 p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
          {children}
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-2xl overflow-hidden flex flex-col shadow-2xl"
        style={{
          width: '90%',
          maxWidth: '800px',
          height: '85vh',
          maxHeight: '85vh'
        }}
      >
        {/* Header */}
        <div className="relative p-6 bg-gradient-to-r from-blue-600 to-indigo-700 text-gray-800">
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <FileText className="w-6 h-6 text-gray-800" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Terms of Service</h2>
                <p className="text-blue-100 text-sm">Service agreement and usage terms</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="small"
              icon={<X className="w-4 h-4" />}
              onClick={onClose}
              className="text-gray-800 hover:bg-white/20 hover:text-gray-100 bg-transparent border-none"
            />
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 ${hasExpandedSections ? 'overflow-y-auto' : 'overflow-hidden'}`}>
          <div className="p-6">
            {/* Intro Section */}
            <div className="text-center mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <div className="text-xs text-gray-600 mb-3 flex items-center justify-center gap-4">
                <span className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <strong>Effective:</strong> January 1, 2025
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <strong>Updated:</strong> January 1, 2025
                </span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                These Terms of Service govern your use of the <span className="font-semibold text-blue-600">fdbk</span> platform and services. 
                By using our services, you agree to these terms and conditions.
              </p>
            </div>

            <div className="space-y-3">
              <CollapsibleSection title="Service Description" sectionKey="service" icon={Server}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4" />
                      Platform Services
                    </h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Tablet-based feedback kiosks for customer data collection</li>
                      <li>Dynamic follow-up questioning capabilities</li>
                      <li>Analytics dashboard and reporting tools</li>
                      <li>Data export and integration features</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold mb-2 text-indigo-800 flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4" />
                      Additional Features
                    </h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Google Reviews connector functionality</li>
                      <li>Custom advertising space management</li>
                      <li>Professional installation and support services</li>
                      <li>Real-time feedback monitoring and alerts</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Subscription and Billing" sectionKey="billing" icon={CreditCard}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 text-sm">Pricing Plans</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-700">Counter Kiosk:</span>
                        <span className="font-medium text-blue-700">$89/month (or $81/month annually)</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-700">Standard Kiosk:</span>
                        <span className="font-medium text-blue-700">$109/month (or $99/month annually)</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-700">Deluxe Kiosk:</span>
                        <span className="font-medium text-blue-700">$249/month (or $227/month annually)</span>
                      </div>
                      <div className="text-xs text-gray-600 mt-2 italic">One-time installation fees apply per unit</div>
                    </div>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold mb-2 text-indigo-800 text-sm">Billing Terms</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Subscriptions are billed in advance</li>
                      <li>Annual plans require upfront payment</li>
                      <li>Installation fees are due upon service completion</li>
                      <li>Late payments may result in service suspension</li>
                      <li>All fees are non-refundable unless required by law</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Equipment and Installation" sectionKey="equipment" icon={Server}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 text-sm">Kiosk Hardware</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Kiosks remain property of fdbk during subscription</li>
                      <li>Professional installation included with setup fee</li>
                      <li>Basic maintenance and technical support provided</li>
                      <li>Customer responsible for physical security and care</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold mb-2 text-indigo-800 text-sm">Network Requirements</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Stable internet connection required (Wi-Fi or ethernet)</li>
                      <li>Minimum bandwidth: 1 Mbps up/down</li>
                      <li>Customer responsible for network setup and costs</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Data Ownership and Usage" sectionKey="data-ownership" icon={Shield}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-r from-green-600 to-emerald-700 text-gray-800 rounded-lg shadow-lg">
                    <p className="font-bold text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      You own your feedback data.
                    </p>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 text-sm">Your Rights</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Customer retains ownership of all collected feedback data</li>
                      <li>Data export available in standard formats</li>
                      <li>Customer responsible for compliance with local privacy laws</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold mb-2 text-indigo-800 text-sm">Our Usage</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>fdbk processes data solely to provide agreed services</li>
                      <li>May use aggregated, anonymized data for service improvement</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Acceptable Use Policy" sectionKey="acceptable-use" icon={AlertTriangle}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200">
                    <h4 className="font-semibold mb-2 text-red-800 text-sm">Prohibited Uses</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Collecting sensitive personal data without consent</li>
                      <li>Using kiosks for illegal or harmful purposes</li>
                      <li>Attempting to reverse engineer or modify hardware/software</li>
                      <li>Interfering with system security or other users</li>
                      <li>Violating applicable laws or regulations</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 text-sm">Content Guidelines</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Custom questions must be appropriate and legal</li>
                      <li>Advertising content subject to approval</li>
                      <li>No discriminatory, offensive, or misleading content</li>
                      <li>Compliance with advertising standards required</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Service Level Agreement" sectionKey="sla" icon={Server}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                    <h4 className="font-semibold mb-2 text-green-800 text-sm">Uptime Commitment</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>99.5% uptime guarantee (excluding scheduled maintenance)</li>
                      <li>Planned maintenance windows: Sundays 2-6 AM local time</li>
                      <li>Service credits available for significant downtime</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 text-sm">Support Response Times</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-700">Critical issues:</span>
                        <span className="font-medium text-blue-700">4-hour response</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-700">Standard issues:</span>
                        <span className="font-medium text-blue-700">24-hour response</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-700">General inquiries:</span>
                        <span className="font-medium text-blue-700">48-hour response</span>
                      </div>
                      <div className="text-xs text-gray-600 mt-2 italic">Support available Monday-Friday, 8 AM - 6 PM local time</div>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Termination and Cancellation" sectionKey="termination" icon={X}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 text-sm">Customer Cancellation</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>30-day written notice required</li>
                      <li>Monthly plans: No early termination fee</li>
                      <li>Annual plans: Remaining balance non-refundable</li>
                      <li>Equipment pickup within 30 days of cancellation</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200">
                    <h4 className="font-semibold mb-2 text-red-800 text-sm">Service Termination by fdbk</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Non-payment: After 30 days past due</li>
                      <li>Terms violation: Immediate termination possible</li>
                      <li>Business closure: 90-day notice provided</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Limitation of Liability" sectionKey="liability" icon={AlertTriangle}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-r from-amber-600 to-orange-700 text-gray-800 rounded-lg shadow-lg">
                    <p className="font-bold text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Important Legal Information
                    </p>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Services provided "as-is" without warranties</li>
                      <li>Liability limited to amount paid in the previous 12 months</li>
                      <li>No liability for indirect, consequential, or punitive damages</li>
                      <li>Customer responsible for data backup and recovery</li>
                      <li>Force majeure events exclude liability</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>
            </div>

            {/* Contact Section */}
            <div className="mt-6 p-4 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg text-gray-800">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2">
                <div className="p-1 bg-white/20 rounded">
                  <Phone className="w-4 h-4" />
                </div>
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="font-semibold text-blue-300 mb-1 text-xs">Legal Department</p>
                  <p className="text-gray-300 text-xs">legal@fdbkinsights.com</p>
                </div>
                <div>
                  <p className="font-semibold text-green-300 mb-1 text-xs">Billing Support</p>
                  <p className="text-gray-300 text-xs">billing@fdbkinsights.com</p>
                </div>
                <div>
                  <p className="font-semibold text-purple-300 mb-1 text-xs">General Support</p>
                  <p className="text-gray-300 text-xs">support@fdbkinsights.com</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TermsOfServiceModal;